import { useTheme } from '@automata/mui-theme';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { FC } from 'react';

import { useWorkcellFormWrapper } from './hooks/useWorkcellFormWrapper';
import { InstrumentSection } from './InstrumentSection';
import { dialogSx, dialogTitleSx } from './styles/workcellFormStyles';
import { TransportConfigurationSection } from './TransportConfigurationSection';
import { WorkcellConfig } from './types';

interface Props {
  defaultWorkcellConfig: WorkcellConfig;
  defaultTransportConfigId?: string;
  initialConfig: WorkcellConfig;
  open: boolean;
  workcellName: string;
  onClose: () => void;
}

export const WorkcellFormModal: FC<Props> = ({
  workcellName,
  open,
  onClose,
  initialConfig,
  defaultWorkcellConfig,
  defaultTransportConfigId,
}) => {
  const theme = useTheme();

  const {
    canSave,
    errors,
    formValues,
    preparedInstruments,
    handleCancel,
    handleTransportConfigChange,
    handleUpdateClick,
    isFieldChanged,
    isFormChanged,
    isInstrumentChanged,
    isTransportConfigChanged,
    resetToDefaultWorkcell,
    resetField,
    transportConfigId,
    transportConfigType,
    transportConfigVersion,
    validateAndUpdate,
    updateSimulateField,
    isSimulateFieldChanged,
  } = useWorkcellFormWrapper({
    defaultWorkcellConfig,
    defaultTransportConfigId,
    initialConfig,
    onClose,
  });

  return (
    <Dialog fullWidth open={open} sx={dialogSx} onClose={onClose}>
      <DialogTitle
        sx={{
          ...dialogTitleSx,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        variant="subtitle1"
      >
        <Box display="flex" alignItems="center" gap={1}>
          Edit {workcellName}
        </Box>
        {isFormChanged(defaultWorkcellConfig) && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              size="small"
              onClick={resetToDefaultWorkcell}
              sx={{ ...theme.typography.overline, paddingBottom: 0 }}
            >
              Reset All
            </Button>
          </Stack>
        )}
      </DialogTitle>
      <DialogContent>
        <TransportConfigurationSection
          changed={isTransportConfigChanged}
          defaultTransportConfigId={defaultTransportConfigId}
          selectedTransportConfigId={transportConfigId}
          selectedTransportConfigType={transportConfigType}
          selectedTransportConfigVersion={transportConfigVersion}
          onChange={handleTransportConfigChange}
        />
        {preparedInstruments.map(([name, instrument]) => (
          <InstrumentSection
            key={name}
            name={name}
            instrument={instrument}
            errors={errors}
            formValues={formValues}
            isFieldChanged={isFieldChanged}
            isInstrumentChanged={isInstrumentChanged}
            resetField={resetField}
            validateAndUpdate={validateAndUpdate}
            updateSimulateField={updateSimulateField}
            isSimulateFieldChanged={isSimulateFieldChanged}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave || !isFormChanged(initialConfig)}
          onClick={handleUpdateClick}
        >
          Save Workcell
        </Button>
      </DialogActions>
    </Dialog>
  );
};
